// NETGRAPH — the network's SVG (BUILD-BRIEF-R5 §6.3). Role is not a colour: shape and stroke
// pattern encode it, exactly as ▪/◇ do in text. Institutions are 14px squares, providers 7px
// circles, a regime or grouped-regime node is a `.u-label` word with no shape at all. Held edges
// are solid, proposed dashed, estimated dotted with the word "estimated" riding beside them —
// the ◇/estimated distinction never borrows the qualifier's own `≈` sign (P12).
//
// Width comes from a `ResizeObserver` on the sheet, NEVER a scaled `viewBox`: 12px text must
// stay 12 real pixels whether the window is wide or narrow. Height is rows × 34, and the sheet
// this component sits in scrolls vertically inside its own overflow — the page body never
// scrolls horizontally.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '../i18n/index.js';
import { ROUTES, open as openRoute } from '../routes.js';
import { quantityText, type NetworkEdge, type NetworkNode, type NetLang } from '../../shared/network.v1.js';
import { layoutNetwork } from './network/layout.js';

export interface NetGraphProps {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
  /** The currently focused node id, or null. Controlled: the page owns it (it is a `ROUTES.network`
   *  arrival intent), NetGraph only reports clicks back through `onFocus`. */
  focus?: string | null;
  onFocus?: (id: string | null) => void;
}

const ROW_HEIGHT = 34;
const SQUARE = 14;
const CIRCLE_R = 7;
/** A label is cut here; the node's <title> keeps the whole name. */
const MAX_NAME = 34;
/** The average advance of one character at `--t-scan` — enough to keep an edge off a label. */
const CHAR_W = 6.6;

export default function NetGraph({ nodes, edges, focus = null, onFocus }: NetGraphProps) {
  const { lang } = useLocale();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const layout = useMemo(() => layoutNetwork(nodes, edges, { focus, width }), [nodes, edges, focus, width]);
  const posById = useMemo(() => new Map(layout.nodes.map((n) => [n.id, n])), [layout]);
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const widthById = useMemo(() => new Map(layout.edges.map((e) => [e.id, e.width])), [layout]);
  const height = Math.max(layout.rows, 1) * ROW_HEIGHT + 10;

  const click = (id: string) => {
    if (!onFocus) return;
    if (focus === id) {
      const n = nodeById.get(id);
      if (n?.organisation_id != null) openRoute(ROUTES.org({ id: n.organisation_id }));
      return;
    }
    onFocus(id);
  };

  // A name is drawn once, cut at MAX_NAME characters with its full text in the node's <title>;
  // an edge never runs THROUGH a label: it starts and ends where the label ends, so a payer's
  // long name is read, not struck through.
  const label = (n: NetworkNode): string => {
    const base = n.kind === 'organisation' ? n.name : `${n.name}${n.members && n.members.length > 0 ? ` (${n.members.length})` : ''}`;
    return base.length > MAX_NAME ? `${base.slice(0, MAX_NAME - 1)}…` : base;
  };
  const shapeR = (n: NetworkNode): number => (n.kind === 'organisation' ? (n.is_institution ? SQUARE / 2 : CIRCLE_R) : 0);
  /** Where a node's label ends on the right (a shape's label starts right of it; a word node is centred). */
  const rightEdge = (n: NetworkNode, x: number): number => (
    n.kind === 'organisation' ? x + shapeR(n) + 6 + label(n).length * CHAR_W : x + (label(n).length * CHAR_W) / 2
  );
  const leftEdge = (n: NetworkNode, x: number): number => (
    n.kind === 'organisation' ? x - shapeR(n) - 3 : x - (label(n).length * CHAR_W) / 2
  );

  return (
    <div ref={wrapRef} style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 560 }}>
      <svg className="net" width={width} height={height} role="group" aria-label="network graph">
        {edges.map((e) => {
          const from = posById.get(e.from);
          const to = posById.get(e.to);
          const fromNode = nodeById.get(e.from);
          const toNode = nodeById.get(e.to);
          if (!from || !to || !fromNode || !toNode) return null;
          // left end and right end, whichever node is which; each end clears its own label
          const [l, r, lNode, rNode] = from.x <= to.x ? [from, to, fromNode, toNode] : [to, from, toNode, fromNode];
          const sx = Math.min(rightEdge(lNode, l.x) + 4, (l.x + r.x) / 2 - 24);
          const ex = Math.max(leftEdge(rNode, r.x), (l.x + r.x) / 2 + 24);
          const midX = (sx + ex) / 2;
          const path = `M ${sx} ${l.y} C ${midX} ${l.y} ${midX} ${r.y} ${ex} ${r.y}`;
          const cls = ['net-edge', e.role === 'unit' && 'net-edge--unit', e.proposed && 'net-edge--proposed', e.method === 'estimated' && 'net-edge--estimated']
            .filter(Boolean).join(' ');
          const w = e.role === 'unit' ? 1 : widthById.get(e.id) ?? 1.5;
          // The figure sits near the COUNTERPART's end (the end that is not the focus, else the
          // `to` end), at t = 0.3 along the curve — every neighbour has its own row, so the
          // labels fan out with the edges instead of piling up at one midpoint.
          const rightId = from.x <= to.x ? e.to : e.from;
          const labelAtRight = focus ? rightId !== focus : rightId === e.to;
          const t = labelAtRight ? 0.7 : 0.3;
          const ease = 3 * t * t - 2 * t * t * t;
          const lx = sx + (ex - sx) * t;
          const ly = l.y + (r.y - l.y) * ease;
          return (
            <g key={e.id}>
              <path className={cls} d={path} strokeWidth={w} />
              <title>{e.title}</title>
              {e.basis && e.quantity != null && (
                <text className="net-fig voice-figure" x={lx} y={ly - 5}>
                  {quantityText(lang as NetLang, e.basis, e.quantity, e.qualifier)}
                </text>
              )}
              {e.method === 'estimated' && (
                <text className="net-tag" x={lx} y={ly + 11}>estimated</text>
              )}
            </g>
          );
        })}
        {layout.nodes.map((pos) => {
          const n = nodeById.get(pos.id);
          if (!n) return null;
          const isFocus = focus === pos.id;
          const isShape = n.kind === 'organisation';
          return (
            <g
              key={pos.id}
              className={`net-node${isFocus ? ' net-node--focus' : ''}`}
              tabIndex={0}
              role="button"
              aria-label={n.name}
              aria-pressed={isFocus}
              onClick={() => click(pos.id)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') click(pos.id); }}
            >
              <title>{n.ref ? `${n.ref} · ${n.name}` : n.name}</title>
              {isShape ? (
                n.is_institution ? (
                  <rect className="net-shape" x={pos.x - SQUARE / 2} y={pos.y - SQUARE / 2} width={SQUARE} height={SQUARE} />
                ) : (
                  <circle className="net-shape" cx={pos.x} cy={pos.y} r={CIRCLE_R} />
                )
              ) : null}
              <text
                className={`net-name${isFocus ? ' net-name--focus' : ''}`}
                x={pos.x + (isShape ? shapeR(n) + 6 : 0)}
                y={pos.y + 4}
                textAnchor={isShape ? 'start' : 'middle'}
              >
                {label(n)}
              </text>
              {isShape && n.ref && (
                <text className="net-ref" x={pos.x + shapeR(n) + 6} y={pos.y + 16}>
                  {n.ref}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
